import { useState } from "react";
import { api } from "../api.js";
import { money, fmtDate } from "../format.js";
import { Async, Button, Loading, useAsync, useToast } from "../ui.jsx";
import DocumentView from "../components/DocumentView.jsx";
import { FlowWordmark } from "../components/Logo.jsx";

/**
 * The issuer's logo, served unauthenticated against the document's own token.
 *
 * Falls back to the wordmark on error rather than leaving a broken image. This is
 * the page the *client* sees, so a missing logo here is the most damaging place for
 * it to show — and it did, because /pub/:kind/:token/logo used to 404 whenever the
 * frozen letterhead pointed at bytes that were gone.
 */
function PublicLogo({ kind, token }) {
  const [failed, setFailed] = useState(false);
  if (failed) return <FlowWordmark height={26} tone="light" />;
  return (
    <img
      src={`/pub/${kind}/${token}/logo`}
      alt=""
      onError={() => setFailed(true)}
      style={{ maxWidth: 150, maxHeight: 46, objectFit: "contain", display: "block" }}
    />
  );
}

/**
 * The page a client sees at /pay/invoice/:token (or /pay/estimate/:token).
 * No sign-in, no app shell — it talks only to the public /pub endpoints.
 */
export default function PublicDocument({ kind, token }) {
  const isInvoice = kind !== "estimate";
  const state = useAsync(() => api.pub.get(`${kind}/${token}`), [kind, token]);

  if (!token) return <Shell><Missing /></Shell>;

  return (
    <Shell>
      {state.loading ? (
        <Loading />
      ) : state.error ? (
        <Missing message={state.error.message} />
      ) : isInvoice ? (
        <PublicInvoice doc={state.data} token={token} />
      ) : (
        <PublicEstimate doc={state.data} token={token} onChanged={state.reload} />
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div style={{ background: "var(--bg)", minHeight: "100vh", padding: "34px 20px" }}>
    <div style={{ maxWidth: 810, margin: "0 auto" }}>{children}</div>
  </div>
);

const Missing = ({ message }) => (
  <div className="card">
    <div className="empty">
      <div className="big">◌</div>
      {message || "That link is no longer valid."}
    </div>
  </div>
);

function PublicInvoice({ doc, token }) {
  const sym = doc.currency_symbol || "$";
  const overdue = doc.status === "overdue";
  const settled = doc.status === "paid";

  return (
    <>
      <div className="card no-print" style={{ padding: 18, marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
        <div>
          <div className="tiny muted">{settled ? "Paid in full" : "Balance due"}</div>
          <div style={{ fontSize: 26, fontWeight: 700 }}>{money(settled ? doc.total : doc.balance, sym)}</div>
          <div className={`tiny ${overdue ? "down" : "muted"}`}>
            {settled
              ? "Thank you — nothing further is owed."
              : overdue
              ? `${doc.days_late} days overdue`
              : `Due ${fmtDate(doc.due_date)}`}
          </div>
        </div>
        <div className="grow" />
        <Button onClick={() => window.open(`/pub/invoice/${token}/pdf`, "_blank")}>⤓ Download PDF</Button>
        <Button onClick={() => window.print()}>Print</Button>
      </div>

      <DocumentView
        doc={doc}
        kind="invoice"
        company={doc.issuer || doc.company}
        client={doc.client}
        logo={doc.has_logo ? <PublicLogo kind="invoice" token={token} /> : null}
      />

      <div className="row no-print" style={{ marginTop: 16, justifyContent: "space-between" }}>
        <FlowWordmark height={14} tone="light" />
        <p className="tiny muted" style={{ margin: 0 }}>
          Questions about this invoice? Reply to the email it came from, or contact {doc.company.email}.
        </p>
      </div>
    </>
  );
}

function PublicEstimate({ doc, token, onChanged }) {
  const toast = useToast();
  const sym = doc.currency_symbol || "$";
  const [busy, setBusy] = useState(null);
  const decided = ["accepted", "declined"].includes(doc.status);

  async function decide(action) {
    setBusy(action);
    try {
      await api.pub.post(`estimate/${token}/${action}`);
      toast.push(action === "accept" ? "Thank you — estimate accepted." : "Estimate declined.");
      onChanged();
    } catch (e) {
      toast.push(e.message, "err");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="card no-print" style={{ padding: 18, marginBottom: 16, display: "flex", alignItems: "center", gap: 14 }}>
        <div>
          <div className="tiny muted">Estimate total</div>
          <div style={{ fontSize: 26, fontWeight: 700 }}>{money(doc.total, sym)}</div>
          <div className="tiny muted">
            {decided
              ? doc.status === "accepted"
                ? "Accepted — thank you."
                : "Declined."
              : doc.expires_on
              ? `Valid until ${fmtDate(doc.expires_on)}`
              : ""}
          </div>
        </div>
        <div className="grow" />
        <Button onClick={() => window.open(`/pub/estimate/${token}/pdf`, "_blank")}>⤓ Download PDF</Button>
        {!decided && (
          <>
            <Button busy={busy === "decline"} onClick={() => decide("decline")}>
              Decline
            </Button>
            <Button kind="pri" busy={busy === "accept"} onClick={() => decide("accept")}>
              Accept estimate
            </Button>
          </>
        )}
      </div>

      <DocumentView
        doc={doc}
        kind="estimate"
        company={doc.issuer || doc.company}
        client={doc.client}
        logo={doc.has_logo ? <PublicLogo kind="estimate" token={token} /> : null}
      />
    </>
  );
}
